import React, { useState, useRef, useEffect } from 'react';
import { Upload, X, Image as ImageIcon, Crop } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { useLanguage } from '@/contexts/LanguageContext';
import { ImageCropDialog } from '@/components/ImageCropDialog';
import { useImageCompression } from '@/hooks/useImageCompression';

interface ImageUploadProps {
  currentImageUrl?: string;
  onImageUpload: (imageUrl: string | null) => void;
  type: 'logo' | 'banner' | 'description' | 'backdrop';
  serverId?: string;
}

type Lang = 'fr' | 'en' | 'es';

function normalizeLang(input?: string | null): Lang {
  const v = String(input ?? '').toLowerCase();
  if (v.startsWith('en')) return 'en';
  if (v.startsWith('es')) return 'es';
  return 'fr';
}

function getLangFromPath(pathname?: string | null): Lang | undefined {
  if (!pathname) return undefined;
  const seg = pathname.replace(/^\/+/, '').split('/')[0]?.toLowerCase();
  if (seg === 'en' || seg === 'es' || seg === 'fr') return seg as Lang;
  return undefined;
}

function resolveUILang(ctxLanguage?: string | null): Lang {
  const pathLang = typeof window !== 'undefined' ? getLangFromPath(window.location.pathname) : undefined;
  if (pathLang) return pathLang;

  const htmlLang = typeof document !== 'undefined' ? document.documentElement.getAttribute('lang') : null;
  if (htmlLang) return normalizeLang(htmlLang);

  if (ctxLanguage) return normalizeLang(ctxLanguage);

  const navLang = typeof navigator !== 'undefined' ? navigator.language : 'fr';
  return normalizeLang(navLang);
}

const TXT: Record<
  Lang,
  {
    uploadBtn: string;
    uploading: string;
    dragHere_prefix: (typeLabel: string) => string;
    selectFile: string;
    typeLabel: (t: 'logo' | 'banner' | 'description' | 'backdrop') => string;
    toastUploaded: (typeLabel: string) => string;
    toastRemoved: string;
    errInvalidType: string;
    errTooLarge: string;
    errUpload: string;
  }
> = {
  fr: {
    uploadBtn: 'Sélectionner un fichier',
    uploading: 'Upload...',
    dragHere_prefix: (typeLabel) => `Glissez votre ${typeLabel} ici ou`,
    selectFile: 'Sélectionner un fichier',
    typeLabel: (t) => (t === 'logo' ? 'logo' : t === 'banner' ? 'bannière' : t === 'backdrop' ? 'image d\'arrière-plan' : 'image de description'),
    toastUploaded: (typeLabel) => `${typeLabel.charAt(0).toUpperCase() + typeLabel.slice(1)} uploadé avec succès.`,
    toastRemoved: 'Image supprimée avec succès.',
    errInvalidType: 'Veuillez sélectionner un fichier image valide.',
    errTooLarge: 'La taille du fichier ne doit pas dépasser 5MB.',
    errUpload: "Échec de l'upload de l'image.",
  },
  en: {
    uploadBtn: 'Choose file',
    uploading: 'Uploading...',
    dragHere_prefix: (typeLabel) => `Drag your ${typeLabel} here or`,
    selectFile: 'Choose file',
    typeLabel: (t) => (t === 'logo' ? 'logo' : t === 'banner' ? 'banner' : t === 'backdrop' ? 'backdrop image' : 'description image'),
    toastUploaded: (typeLabel) => `${typeLabel.charAt(0).toUpperCase() + typeLabel.slice(1)} uploaded successfully.`,
    toastRemoved: 'Image removed successfully.',
    errInvalidType: 'Please select a valid image file.',
    errTooLarge: 'File size must not exceed 5MB.',
    errUpload: 'Failed to upload the image.',
  },
  es: {
    uploadBtn: 'Seleccionar archivo',
    uploading: 'Subiendo...',
    dragHere_prefix: (typeLabel) => `Arrastra tu ${typeLabel} aquí o`,
    selectFile: 'Seleccionar archivo',
    typeLabel: (t) => (t === 'logo' ? 'logo' : t === 'banner' ? 'banner' : t === 'backdrop' ? 'imagen de fondo' : 'imagen de descripción'),
    toastUploaded: (typeLabel) => `${typeLabel.charAt(0).toUpperCase() + typeLabel.slice(1)} subido correctamente.`,
    toastRemoved: 'Imagen eliminada correctamente.',
    errInvalidType: 'Selecciona un archivo de imagen válido.',
    errTooLarge: 'El archivo no debe superar 5MB.',
    errUpload: 'Error al subir la imagen.',
  },
};

export const ImageUpload: React.FC<ImageUploadProps> = ({
  currentImageUrl,
  onImageUpload,
  type,
  serverId
}) => {
  const { t, language: ctxLanguage } = useLanguage();
  const uiLang = resolveUILang(ctxLanguage);
  const L = TXT[uiLang];
  const { compressImageFile, isCompressing } = useImageCompression();

  const [isUploading, setIsUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(currentImageUrl || '');
  const [cropDialogOpen, setCropDialogOpen] = useState(false);
  const [tempImageForCrop, setTempImageForCrop] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setPreviewUrl(currentImageUrl || '');
  }, [currentImageUrl]);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    
    // Réinitialiser l'input pour permettre de sélectionner le même fichier
    if (event.target) {
      event.target.value = '';
    }
    
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast({
        title: t('notifications.error'),
        description: L.errInvalidType,
        variant: 'destructive',
      });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: t('notifications.error'),
        description: L.errTooLarge,
        variant: 'destructive',
      });
      return;
    }

    // Pour banner, description et backdrop, ouvrir le dialogue de crop
    if (type === 'banner' || type === 'description' || type === 'backdrop') {
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result as string;
        if (result) {
          setTempImageForCrop(result);
          // Utiliser setTimeout pour s'assurer que le state est bien mis à jour avant d'ouvrir le dialogue
          setTimeout(() => {
            setCropDialogOpen(true);
          }, 50);
        }
      };
      reader.onerror = () => {
        toast({
          title: t('notifications.error'),
          description: L.errUpload,
          variant: 'destructive',
        });
      };
      reader.readAsDataURL(file);
    } else {
      // Pour logo, upload direct
      await uploadImage(file);
    }
  };

  const handleCropComplete = async (croppedBlob: Blob) => {
    const croppedFile = new File([croppedBlob], 'cropped-image.jpg', { type: 'image/jpeg' });
    await uploadImage(croppedFile);
    setTempImageForCrop(null);
  };

  const uploadImage = async (file: File) => {
    setIsUploading(true);

    try {
      // Déterminer les dimensions optimales selon le type d'image
      const compressionOptions = {
        maxWidth: type === 'logo' ? 512 : type === 'banner' ? 1920 : 1600,
        maxHeight: type === 'logo' ? 512 : type === 'banner' ? 640 : 900,
        quality: 0.8,
        format: 'webp' as const,
        maintainAspectRatio: true
      };

      // Compresser l'image avant l'upload
      const compressedBlob = await compressImageFile(file, compressionOptions);
      
      if (!compressedBlob) {
        throw new Error('Image compression failed');
      }

      // Créer un fichier avec l'extension .webp
      const fileName = `${serverId || 'temp'}_${type}_${Date.now()}.webp`;
      const filePath = `${type}s/${fileName}`;
      
      // Convertir le Blob en File
      const compressedFile = new File([compressedBlob], fileName, { type: 'image/webp' });

      const { error: uploadError } = await supabase.storage
        .from('server-images')
        .upload(filePath, compressedFile, {
          cacheControl: '31536000', // 1 an de cache
          upsert: false
        });

      if (uploadError) {
        throw uploadError;
      }

      const { data } = supabase.storage
        .from('server-images')
        .getPublicUrl(filePath);

      const imageUrl = data.publicUrl;
      setPreviewUrl(imageUrl);
      onImageUpload(imageUrl);

      const typeLabel = L.typeLabel(type);
      toast({
        title: t('notifications.success'),
        description: L.toastUploaded(typeLabel),
      });

    } catch (error) {
      console.error('Erreur upload:', error);
      toast({
        title: t('notifications.error'),
        description: L.errUpload,
        variant: 'destructive',
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemoveImage = async () => {
    if (previewUrl && previewUrl.includes('supabase')) {
      try {
        const urlParts = previewUrl.split('/');
        const bucketIndex = urlParts.findIndex(part => part === 'server-images');
        if (bucketIndex !== -1 && bucketIndex < urlParts.length - 1) {
          const filePath = urlParts.slice(bucketIndex + 1).join('/');

          const { error } = await supabase.storage
            .from('server-images')
            .remove([filePath]);

          if (error) {
            console.warn('Erreur lors de la suppression du fichier:', error);
          }
        }
      } catch (error) {
        console.warn('Erreur lors de la suppression du fichier:', error);
      }
    }

    setPreviewUrl('');
    onImageUpload(null);

    toast({
      title: t('notifications.success'),
      description: L.toastRemoved,
    });
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      if (file.type.startsWith('image/')) {
        uploadImage(file);
      } else {
        toast({
          title: t('notifications.error'),
          description: L.errInvalidType,
          variant: 'destructive',
        });
      }
    }
  };

  const typeLabel = L.typeLabel(type);
  
  const getAspectRatio = () => {
    if (type === 'banner') return 3; // 1200x400
    if (type === 'description') return 16 / 9;
    if (type === 'logo') return 1;
    return 16 / 9;
  };

  return (
    <>
      <ImageCropDialog
        open={cropDialogOpen}
        onClose={() => {
          setCropDialogOpen(false);
          setTempImageForCrop(null);
        }}
        imageSrc={tempImageForCrop || ''}
        onCropComplete={handleCropComplete}
        aspectRatio={getAspectRatio()}
        cropShape={type === 'logo' ? 'round' : 'rect'}
      />
      
      <div className="space-y-2">
        <div
          className={`relative border-2 border-dashed rounded-lg p-4 text-center transition-colors ${
            isUploading ? 'border-purple-500 bg-purple-50' : 'border-slate-600 hover:border-purple-400'
          }`}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          {previewUrl ? (
            <div className="relative">
              <img
                src={previewUrl}
                alt={`Preview ${type}`}
                className={`mx-auto rounded object-cover ${
                  type === 'logo' ? 'w-24 h-24' : type === 'description' ? 'w-full h-48' : type === 'backdrop' ? 'w-full h-40' : 'w-full h-32'
                }`}
              />
              <Button
                type="button"
                variant="destructive"
                size="sm"
                className="absolute top-2 right-2"
                onClick={handleRemoveImage}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              <ImageIcon className="w-12 h-12 mx-auto text-slate-400" />
              <div>
                <p className="text-slate-300 text-sm">
                  {L.dragHere_prefix(typeLabel)}
                </p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="mt-2"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploading}
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {isUploading || isCompressing ? L.uploading : L.uploadBtn}
                </Button>
              </div>
            </div>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
          />
        </div>

        <p className="text-xs text-slate-400">
          {t('imageUpload.supportedFormats')}
          {type === 'logo' && t('imageUpload.recommendedSize.logo')}
          {type === 'banner' && t('imageUpload.recommendedSize.banner')}
          {type === 'backdrop' && t('imageUpload.recommendedSize.backdrop')}
          {type === 'description' && t('imageUpload.recommendedSize.description')}
        </p>
        
        {(type === 'banner' || type === 'description' || type === 'backdrop') && (
          <div className="flex items-center gap-2 text-xs text-gaming-primary">
            <Crop className="w-3 h-3" />
            <span>{t('imageUpload.cropInfo')}</span>
          </div>
        )}
      </div>
    </>
  );
};